import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { teamMembers } from "../../mocks/magazine";
import { Link } from "react-router-dom";
import SEOHead from "../../components/feature/SEOHead";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? "";

const aboutSchema: Record<string, unknown>[] = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About The Magazine Co. | Independent Print Publishing for Creators",
    url: `${siteUrl}/about`,
    description:
      "Learn about The Magazine Co. — our mission, values, team, and how we help creators launch their own print publications.",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "About", item: `${siteUrl}/about` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "The Magazine Co.",
    url: siteUrl,
    foundingDate: "2019",
    numberOfEmployees: { "@type": "QuantitativeValue", value: 30 },
    description: "The Magazine Co. helps creators launch their own print publications, reaching subscribers worldwide.",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+16786633900",
      contactType: "customer support",
      email: "makeitamagazine@gmail.com",
      availableLanguage: "English",
      hoursAvailable: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "16:00",
      },
    },
  },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About The Magazine Co. | Independent Print Publishing for Creators"
        description="Since 2019, The Magazine Co. has helped creators launch their own print magazines, reaching subscribers worldwide. Learn our story, mission, and team."
        keywords="about The Magazine Co., print publishing company, independent magazine publishing, creator publishing platform"
        path="/about"
      />
      <JsonLd id="about-schema" schema={aboutSchema} />
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-40 pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/0441e1f107e2c2a33793009c7916ce42.jpg"
            alt="Our studio"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/50" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#F0EAD6] text-xs tracking-[0.3em] uppercase font-semibold mb-5">About Us</p>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-white leading-[1.1] mb-8">
            We Believe in the<br /><em className="italic">Power of Print</em>
          </h1>
          <p className="text-white/75 text-lg md:text-xl font-light max-w-2xl mx-auto leading-relaxed">
            In a world of endless scrolling, we create something you can hold, collect, and treasure. A physical magazine made entirely from your ideas.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-[#F8F4EE] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#C41E3A] text-xs tracking-[0.3em] uppercase font-semibold mb-5">Our Mission</p>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-[#2B2B2B] leading-tight mb-8">
              Giving Creators a<br />Permanent Voice
            </h2>
            <p className="text-[#6B6B6B] text-base leading-relaxed mb-5">
              The Magazine Co. was founded on a simple belief: the best content deserves more than a fleeting moment on a screen. Independent creators — writers, photographers, artists, thinkers — deserve to see their work bound, printed, and distributed to hands around the world.
            </p>
            <p className="text-[#6B6B6B] text-base leading-relaxed mb-5">
              We handle everything from editorial design and layout to premium printing and global logistics. All you need to do is create.
            </p>
            <p className="text-[#6B6B6B] text-base leading-relaxed">
              Since 2019, we&apos;ve helped creators launch their own publications — reaching subscribers in dozens of countries, with over a million magazines printed and delivered.
            </p>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden" style={{ aspectRatio: "4/3" }}>
              <img
                src="https://readdy.ai/api/search-image?query=close%20up%20of%20high%20quality%20magazine%20pages%20being%20examined%20by%20a%20designer%2C%20warm%20studio%20environment%2C%20editorial%20design%20workspace%2C%20beautiful%20typography%20and%20photography%20visible%20on%20pages%2C%20soft%20warm%20lighting%2C%20cream%20and%20tan%20background&width=800&height=600&seq=402&orientation=landscape"
                alt="Editorial design process"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#C41E3A] rounded-2xl p-6 text-white hidden md:block">
              <p className="text-3xl font-serif font-light">1.2M+</p>
              <p className="text-sm text-white/80 mt-1">Magazines Printed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <p className="text-[#C41E3A] text-xs tracking-[0.3em] uppercase font-semibold mb-4">What We Stand For</p>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-[#2B2B2B]">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "ri-award-line",
                title: "Craft Over Quantity",
                body: "Every issue we produce is treated as a work of art. We never cut corners on paper quality, binding, or color accuracy.",
              },
              {
                icon: "ri-user-heart-line",
                title: "Creator First",
                body: "Your creative vision is sacred. Our editorial team enhances your content — never overrides it.",
              },
              {
                icon: "ri-leaf-line",
                title: "Sustainable Production",
                body: "We use FSC-certified paper and carbon-offset shipping on all our distribution routes globally.",
              },
              {
                icon: "ri-global-line",
                title: "Global Reach",
                body: "From a single reader in São Paulo to 10,000 subscribers in Tokyo — we get your magazine there.",
              },
              {
                icon: "ri-time-line",
                title: "Reliability",
                body: "Your readers expect their issue on time. We&apos;ve maintained a 99.7% on-schedule delivery rate since day one.",
              },
              {
                icon: "ri-handshake-line",
                title: "Long-term Partnership",
                body: "We grow with you. As your readership expands, our capacity, pricing, and support scales with you.",
              },
            ].map((value) => (
              <div key={value.title} className="bg-[#F8F4EE] rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white mb-5">
                  <span className="text-[#C41E3A] text-xl flex items-center justify-center w-6 h-6">
                    <i className={value.icon} />
                  </span>
                </div>
                <h3 className="font-serif text-xl text-[#2B2B2B] mb-3">{value.title}</h3>
                <p className="text-[#6B6B6B] text-sm leading-relaxed">{value.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-[#F8F4EE] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <p className="text-[#C41E3A] text-xs tracking-[0.3em] uppercase font-semibold mb-4">The People</p>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-[#2B2B2B]">Meet the Team</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <div key={member.id} className="group">
                <div className="rounded-2xl overflow-hidden mb-5" style={{ aspectRatio: "1/1" }}>
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-serif text-xl text-[#2B2B2B]">{member.name}</h3>
                <p className="text-[#C41E3A] text-sm font-medium mt-1 mb-3">{member.role}</p>
                <p className="text-[#6B6B6B] text-sm leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 text-center border-t border-[#E8E2DA]">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-[#C41E3A] text-xs tracking-[0.3em] uppercase font-semibold mb-5">Work With Us</p>
          <h2 className="text-4xl font-serif font-light text-[#1C1C1C] mb-5">Ready to Work With Us?</h2>
          <p className="text-[#5A5A5A] text-lg mb-8 font-light leading-relaxed">
            Let&apos;s talk about your content, your audience, and how we can build your magazine together.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C41E3A] text-white text-sm font-semibold tracking-wide whitespace-nowrap hover:bg-[#A0182E] transition-colors cursor-pointer"
          >
            Get in Touch
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
